import { useEffect, useRef, useState } from "react";
import { Plus, Sparkles, Trash2, Loader2, Camera, Paperclip, Archive } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { cacheGet, cacheSet, isNetworkError, queueAdd, queueRemove, useQueue } from "@/lib/offline";
import { useLang } from "@/lib/i18n";
import { VAT_TYPES } from "@/lib/translations";
import { r2, today, VAT_RATE, yearStart } from "@/lib/format";
import { DateRange, EmptyRow, Field, Money, NativeSelect, PageHeader, Panel } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ReceiptPreview, ReceiptThumb } from "@/components/Receipt";

const EMPTY = { date: today(), vendor: "", description: "", amount: "", vat_type: "standard", account_id: "", payment_code: "1100", ai_categorized: false, receipt_path: "" };
const AiBadge = ({ label }) => <span className="inline-flex items-center gap-1 rounded bg-gradient-to-r from-amber-500 to-amber-600 px-1.5 py-0.5 text-[10px] font-bold text-white shadow-sm"><Sparkles className="h-3 w-3" />{label}</span>;

function ExpenseDialog({ open, onClose, accounts, onSaved }) {
  const { t, accName } = useLang();
  const [f, setF] = useState(EMPTY);
  const [ai, setAi] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setF({ ...EMPTY, date: today() }); setAi(null); } }, [open]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value, ...(k === "account_id" ? { ai_categorized: false } : {}) });
  const amount = Number(f.amount) || 0;
  const vat = r2(amount * VAT_RATE[f.vat_type]);

  const scanRef = useRef();
  const attachRef = useRef();
  const upload = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
    return data.path;
  };
  const attach = async (file) => {
    if (!file) return;
    setBusy(true);
    try { const p = await upload(file); setF((prev) => ({ ...prev, receipt_path: p })); toast.success(t("receiptAttached")); }
    catch (e) { toast.error(errMsg(e) || t("uploadFailed")); }
    setBusy(false);
    if (attachRef.current) attachRef.current.value = "";
  };
  const scan = async (file) => {
    if (!file) return;
    setBusy(true);
    let path = f.receipt_path;
    try { path = await upload(file); } catch (e) { toast.error(errMsg(e) || t("uploadFailed")); }
    try {
      const b64 = await new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(file); });
      const { data } = await api.post("/ai/scan-receipt", { image_base64: b64 });
      setF({ ...f, receipt_path: path, vendor: data.vendor || f.vendor, description: data.description || f.description, amount: data.amount ? String(data.amount) : f.amount,
        date: data.date || f.date, vat_type: data.vat ? "standard" : "exempt", account_id: data.account_id || f.account_id, ai_categorized: !!data.account_id });
      setAi(data.account_id ? { reason: `${t("aiTag")} · ${Math.round((data.confidence || 0) * 100)}%` } : null);
      toast.success(t("receiptScanned"));
    } catch (e) { setF((prev) => ({ ...prev, receipt_path: path })); toast.error(errMsg(e) === "PLAN_AI" ? t("planAiLocked") : t("scanFailed")); }
    setBusy(false);
    if (scanRef.current) scanRef.current.value = "";
  };
  const suggest = async () => {
    if (!f.vendor) return toast.error(t("vendor"));
    setBusy(true);
    try {
      const { data } = await api.post("/ai/categorize", { vendor: f.vendor, description: f.description, amount });
      setF((p) => ({ ...p, account_id: data.account_id, ai_categorized: true }));
      setAi(data);
    } catch (e) { toast.error(errMsg(e) === "PLAN_AI" ? t("planAiLocked") : t("aiError")); }
    setBusy(false);
  };
  const submit = async (e) => {
    e.preventDefault();
    const body = { ...f, amount };
    try {
      await api.post("/expenses", body);
      toast.success(t("saved"));
      onSaved();
    } catch (err) {
      if (isNetworkError(err)) { queueAdd(body); toast.info(t("savedOffline")); onSaved(); }
      else toast.error(errMsg(err));
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="expense-dialog" className="max-w-xl">
        <DialogHeader><DialogTitle className="font-display">{t("newExpense")}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="grid grid-cols-2 gap-4">
          <Field label={t("date")}><Input data-testid="expense-date-input" type="date" className="font-mono" value={f.date} onChange={set("date")} required /></Field>
          <Field label={t("vendor")}><Input data-testid="expense-vendor-input" value={f.vendor} onChange={set("vendor")} required /></Field>
          <Field label={t("description")} className="col-span-2"><Input data-testid="expense-desc-input" value={f.description} onChange={set("description")} /></Field>
          <Field label={t("amountNet")}><Input data-testid="expense-amount-input" type="number" min="0.01" step="0.01" className="font-mono" value={f.amount} onChange={set("amount")} required /></Field>
          <Field label={t("vatType")}>
            <NativeSelect data-testid="expense-vat-select" value={f.vat_type} onChange={set("vat_type")}>{VAT_TYPES.map((v) => <option key={v} value={v}>{t(`vat_${v}`)}</option>)}</NativeSelect>
          </Field>
          <div className="col-span-2 grid grid-cols-2 gap-2">
            <input ref={scanRef} type="file" accept="image/*" capture="environment" className="hidden" data-testid="expense-scan-input" onChange={(e) => scan(e.target.files?.[0])} />
            <input ref={attachRef} type="file" accept="image/*,application/pdf" className="hidden" data-testid="expense-attach-input" onChange={(e) => attach(e.target.files?.[0])} />
            <Button type="button" data-testid="expense-scan-btn" onClick={() => scanRef.current?.click()} disabled={busy} variant="outline" className="border-dashed border-[#007A3D]/40 text-[#007A3D] hover:bg-emerald-50">
              {busy ? <Loader2 className="me-1 h-4 w-4 animate-spin" /> : <Camera className="me-1 h-4 w-4" />}{t("scanReceipt")}
            </Button>
            <Button type="button" data-testid="expense-attach-btn" onClick={() => attachRef.current?.click()} disabled={busy} variant="outline" className="border-dashed text-slate-700">
              <Paperclip className="me-1 h-4 w-4" />{f.receipt_path ? t("receiptAttached") : t("attachReceipt")}
            </Button>
            {f.receipt_path && <div className="col-span-2 flex items-center gap-2 text-xs text-slate-600"><ReceiptThumb path={f.receipt_path} testid="expense-form-receipt-thumb" /><span data-testid="expense-form-receipt-ok">{t("receiptKeptNote")}</span></div>}
          </div>
          <div className="col-span-2">
            <Field label={t("category")}>
              <div className="flex gap-2">
                <NativeSelect data-testid="expense-category-select" value={f.account_id} onChange={set("account_id")} required>
                  <option value="">{t("selectCategory")}</option>
                  {accounts.map((a) => <option key={a.id} value={a.id}>{a.code} · {accName(a)}</option>)}
                </NativeSelect>
                <Button type="button" data-testid="expense-ai-suggest-btn" onClick={suggest} disabled={busy} className="shrink-0 bg-[#0B1220] text-white hover:bg-slate-800">
                  {busy ? <Loader2 className="me-1 h-4 w-4 animate-spin" /> : <Sparkles className="me-1 h-4 w-4 text-[#D4AF37]" />}{busy ? t("suggesting") : t("aiSuggest")}
                </Button>
              </div>
            </Field>
            {ai && f.ai_categorized && (
              <div data-testid="expense-ai-result" className="mt-2 flex items-start gap-2 rounded-md border border-[#D4AF37]/40 bg-[#FFFBEB] p-2.5 text-xs text-slate-700">
                <AiBadge label={`${Math.round(ai.confidence * 100)}%`} /><span>{ai.reason}</span>
              </div>
            )}
          </div>
          <Field label={t("paymentFrom")}>
            <NativeSelect data-testid="expense-payment-select" value={f.payment_code} onChange={set("payment_code")}>
              <option value="1100">{t("bank")}</option><option value="1000">{t("cash")}</option><option value="2000">{t("payable")}</option>
            </NativeSelect>
          </Field>
          <div className="flex flex-col justify-end text-end text-sm">
            <span className="text-slate-500">{t("vat")}: <Money value={vat} /></span>
            <span className="font-bold">{t("total")}: <Money testid="expense-total-preview" value={r2(amount + vat)} /></span>
          </div>
          <div className="col-span-2 flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={onClose}>{t("cancel")}</Button>
            <Button data-testid="expense-save-btn" className="bg-[#007A3D] hover:bg-[#005E2E]">{t("save")}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function FtaExport() {
  const { t } = useLang();
  const [range, setRange] = useState({ start: yearStart(), end: today() });
  const [busy, setBusy] = useState(false);
  const dl = async () => {
    setBusy(true);
    try {
      const res = await api.get("/export/fta-receipts", { params: range, responseType: "blob" });
      const name = /filename="([^"]+)"/.exec(res.headers["content-disposition"] || "")?.[1] || "fta-export.zip";
      const url = URL.createObjectURL(res.data);
      Object.assign(document.createElement("a"), { href: url, download: name }).click();
      URL.revokeObjectURL(url);
      toast.success(t("exportFtaDone"));
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <Panel className="mb-6 flex flex-wrap items-end justify-between gap-4 p-4" data-testid="fta-export-panel">
      <div className="max-w-md">
        <div className="flex items-center gap-2 font-display text-sm font-bold text-slate-900"><Archive className="h-4 w-4 text-[#D4AF37]" />{t("exportFta")}</div>
        <p className="mt-1 text-xs text-slate-500">{t("exportFtaHint")}</p>
      </div>
      <div className="flex flex-wrap items-end gap-3">
        <DateRange testid="fta-range" start={range.start} end={range.end} onChange={(start, end) => setRange({ start, end })} />
        <Button data-testid="fta-export-btn" onClick={dl} disabled={busy} variant="outline" className="h-10 border-[#D4AF37]/60 text-slate-900 hover:bg-[#FFFBEB]">
          {busy ? <Loader2 className="me-1 h-4 w-4 animate-spin" /> : <Archive className="me-1 h-4 w-4" />}{t("downloadZip")}
        </Button>
      </div>
    </Panel>
  );
}

export default function Expenses() {
  const { t, accName } = useLang();
  const [rows, setRows] = useState(() => cacheGet("expenses") || []);
  const [accounts, setAccounts] = useState(() => cacheGet("expense_accounts") || []);
  const [open, setOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const queue = useQueue();
  const load = () => api.get("/expenses").then((r) => { setRows(r.data); cacheSet("expenses", r.data); }).catch(() => {});
  useEffect(() => {
    load();
    api.get("/accounts").then((r) => { const a = r.data.filter((x) => x.type === "expense"); setAccounts(a); cacheSet("expense_accounts", a); }).catch(() => {});
    const h = () => load();
    window.addEventListener("mohassib:synced", h);
    return () => window.removeEventListener("mohassib:synced", h);
  }, []);
  const byId = Object.fromEntries(accounts.map((a) => [a.id, a]));
  const remove = async (x) => {
    if (!window.confirm(t("confirmDelete"))) return;
    await api.delete(`/expenses/${x.id}`);
    toast.success(t("deleted"));
    load();
  };
  return (
    <div data-testid="expenses-page">
      <PageHeader title={t("expenses")} subtitle={t("expensesSubtitle")}
        actions={<Button data-testid="new-expense-btn" onClick={() => setOpen(true)} className="bg-[#007A3D] hover:bg-[#005E2E]"><Plus className="me-1 h-4 w-4" />{t("newExpense")}</Button>} />
      <FtaExport />
      <Panel className="overflow-x-auto">
        <table className="data-table">
          <thead><tr><th>{t("date")}</th><th>{t("vendor")}</th><th>{t("description")}</th><th>{t("category")}</th><th>{t("receipt")}</th><th className="text-end">{t("net")}</th><th className="text-end">{t("vat")}</th><th className="text-end">{t("total")}</th><th /></tr></thead>
          <tbody>
            {rows.length === 0 && queue.length === 0 && <EmptyRow cols={9} />}
            {queue.map((x) => (
              <tr key={x._offline_id} data-testid={`expense-pending-${x._offline_id}`} className="bg-amber-50/60">
                <td className="font-mono text-slate-500">{x.date}</td>
                <td className="font-semibold text-slate-900">{x.vendor}</td>
                <td className="text-slate-600">{x.description}</td>
                <td><span className="inline-flex items-center gap-2">{accName(byId[x.account_id])}<span className="rounded-full bg-amber-200 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-amber-900">{t("pendingSync")}</span></span></td>
                <td><span className="text-xs text-slate-300">—</span></td>
                <td className="text-end"><Money value={x.amount} cur={false} /></td>
                <td className="text-end"><Money value={r2(x.amount * VAT_RATE[x.vat_type])} cur={false} /></td>
                <td className="text-end font-semibold"><Money value={r2(x.amount * (1 + VAT_RATE[x.vat_type]))} /></td>
                <td className="text-end"><button data-testid={`discard-pending-${x._offline_id}`} onClick={() => queueRemove(x._offline_id)} className="rounded p-1.5 text-slate-500 hover:bg-red-50 hover:text-[#CE1126]"><Trash2 className="h-4 w-4" /></button></td>
              </tr>
            ))}
            {rows.map((x) => (
              <tr key={x.id} data-testid={`expense-row-${x.id}`}>
                <td className="font-mono text-slate-500">{x.date}</td>
                <td className="font-semibold text-slate-900">{x.vendor}</td>
                <td className="text-slate-600">{x.description}</td>
                <td><span className="inline-flex items-center gap-2">{accName(byId[x.account_id])}{x.ai_categorized && <AiBadge label={t("aiTag")} />}</span></td>
                <td>{x.receipt_path ? <ReceiptThumb path={x.receipt_path} testid={`expense-receipt-${x.id}`} onClick={() => setPreview(x)} /> : <span className="text-xs text-slate-300">—</span>}</td>
                <td className="text-end"><Money value={x.amount} cur={false} /></td>
                <td className="text-end"><Money value={x.vat_amount} cur={false} /></td>
                <td className="text-end font-semibold"><Money value={x.total} /></td>
                <td className="text-end"><button data-testid={`delete-expense-${x.id}`} onClick={() => remove(x)} className="rounded p-1.5 text-slate-500 hover:bg-red-50 hover:text-[#CE1126]"><Trash2 className="h-4 w-4" /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </Panel>
      <ExpenseDialog open={open} accounts={accounts} onClose={() => setOpen(false)} onSaved={() => { setOpen(false); load(); }} />
      <ReceiptPreview expense={preview} onClose={() => setPreview(null)} />
    </div>
  );
}
